import { useMemo, useState } from 'react'
import { Loader2, MapPin, RefreshCw, Truck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { ParcelTracking } from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { TranslateToggle } from '@/src/components/translation/Translatable'
import { useTranslated } from '@/src/components/translation/useTranslated'
import { ParcelRouteMap } from '@/src/components/tracking/ParcelRouteMap'
import { TRACKING_STATUS, formatTrackingTime, routeStops, stopTitle } from '@/src/lib/parcels'

/**
 * A parcel's courier tracking: who carries it, where it is now, the map of
 * where it has been and every update, newest first. Customers see it on their
 * parcels; the warehouse team also sees how registering the number went and
 * can ask the tracking service again.
 */

const REGISTRATION_LABELS: Record<NonNullable<ParcelTracking['registration']>['status'], string> = {
  PENDING: 'Registering with the tracking service',
  REGISTERED: 'Followed by the tracking service',
  FAILED: 'Registration failed',
  STOPPED: 'No longer followed',
}

const PROVIDER_LABELS: Record<string, string> = { '17TRACK': '17TRACK', KUAIDI100: 'Kuaidi100' }

/** Courier and status in one line, with the latest place: for lists. */
export function ParcelTrackingSummary({ tracking }: { tracking: ParcelTracking }) {
  const status = TRACKING_STATUS[tracking.status]
  const place = tracking.last_event?.location ?? tracking.current_location?.name
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <Badge variant="outline" className={cn('py-0.5', status.tone)}>
        <Truck aria-hidden />
        {status.label}
      </Badge>
      {tracking.carrier_name && <span className="font-medium">{tracking.carrier_name}</span>}
      {place && (
        <span className="text-muted-foreground">
          {place}
          {tracking.last_event ? ` · ${formatTrackingTime(tracking.last_event.at)}` : ''}
        </span>
      )}
    </div>
  )
}

export function ParcelTrackingPanel({
  tracking,
  title,
  onRefresh,
  initiallyOpen = false,
  statusLabels,
}: {
  tracking: ParcelTracking
  /** What the parcel is, for the map's and toggle's names. */
  title: string
  /** Staff only: asks the tracking service again; resolves to an error message or null. */
  onRefresh?: () => Promise<string | null>
  initiallyOpen?: boolean
  /** Words for the courier's status where the default ones would mislead. */
  statusLabels?: Partial<Record<ParcelTracking['status'], string>>
}) {
  const [open, setOpen] = useState(initiallyOpen)
  const [showAll, setShowAll] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const stops = useMemo(() => routeStops(tracking), [tracking])
  // The stop picked on the map or in the timeline; its scans are highlighted.
  const [selected, setSelected] = useState<number | null>(null)
  const stopByEvent = useMemo(
    () => new Map(stops.flatMap((stop) => stop.eventIds.map((id) => [id, stop.number] as const))),
    [stops],
  )
  const selectedStop = stops.find((stop) => stop.number === selected) ?? null
  const pick = (number: number | null) => {
    const next = number === selected ? null : number
    setSelected(next)
    const stop = stops.find((candidate) => candidate.number === next)
    // Its scans may sit below the first few updates.
    if (stop && tracking.events.slice(4).some((event) => stop.eventIds.includes(event.id))) {
      setShowAll(true)
    }
  }
  const status = {
    ...TRACKING_STATUS[tracking.status],
    label: statusLabels?.[tracking.status] ?? TRACKING_STATUS[tracking.status].label,
  }
  const latest = tracking.last_event
  const here = tracking.current_location
  const events = showAll ? tracking.events : tracking.events.slice(0, 4)
  const panelId = `tracking-${tracking.tracking_number}`
  // The courier's own words, read in the person's language on request: the
  // latest place and scan, then each update's scan and place.
  const [observeScans, scans] = useTranslated(
    [
      latest?.location ?? here?.name,
      latest?.description,
      ...tracking.events.flatMap((event) => [event.description, event.location]),
    ],
    'courier_scan',
  )
  const eventIndex = new Map(tracking.events.map((event, index) => [event.id, index]))
  const scanOf = (event: ParcelTracking['events'][number]) =>
    event.description ? scans.text(2 + 2 * eventIndex.get(event.id)!) : 'Courier scan'
  const placeOf = (event: ParcelTracking['events'][number]) =>
    event.location ? scans.text(3 + 2 * eventIndex.get(event.id)!) : null

  const refresh = async () => {
    if (!onRefresh) return
    setRefreshing(true)
    setRefreshError(null)
    try {
      setRefreshError(await onRefresh())
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <section
      ref={observeScans}
      className="space-y-3 rounded-lg border p-3 text-sm"
      aria-label={`Tracking for ${title}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={cn('py-1', status.tone)}>
          <Truck aria-hidden />
          {status.label}
        </Badge>
        <span className="font-medium">{tracking.carrier_name ?? 'Courier not known yet'}</span>
      </div>

      {latest ? (
        <div className="space-y-1">
          <p className="flex items-start gap-2 text-base font-semibold">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {(latest.location ?? here?.name) ? scans.text(0) : 'Place not given'}
              <span className="block text-xs font-normal text-muted-foreground">
                {formatTrackingTime(latest.at)}
              </span>
            </span>
          </p>
          {latest.description && <p className="break-words">{scans.text(1)}</p>}
          <TranslateToggle block={scans} label={`the courier's updates for ${title}`} />
        </div>
      ) : (
        <p className="text-muted-foreground">
          The courier has not scanned it yet. That is normal for a day or two after the seller sends
          it; it updates here on its own.
        </p>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-10 sm:h-8"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? 'Hide map and updates' : 'Show map and updates'}
      </Button>

      {open && (
        <div id={panelId} className="space-y-3">
          {stops.length > 0 ? (
            <figure className="space-y-1.5">
              <ParcelRouteMap
                stops={stops}
                label={`Map of where ${title} has been${here?.name ? `; now in ${here.name}` : ''}`}
                selected={selected}
                onSelect={(stop) => pick(stop.number)}
              />
              <figcaption className="text-xs text-muted-foreground">
                Approximate: the courier&apos;s scan areas
                {selectedStop ? (
                  <span className="block font-medium text-foreground" aria-live="polite">
                    Stop {selectedStop.number}: {stopTitle(selectedStop)}
                  </span>
                ) : null}
              </figcaption>
            </figure>
          ) : (
            <div className="flex h-24 items-center justify-center rounded-lg border border-dashed text-muted-foreground">
              Location not available yet
            </div>
          )}

          {tracking.events.length > 0 && (
            <div>
              <ol className="space-y-3 border-l pl-4" aria-label={`Updates for ${title}`}>
                {events.map((event, index) => {
                  const stop = stopByEvent.get(event.id)
                  const highlighted = stop != null && stop === selected
                  const content = (
                    <>
                      <p className="text-xs text-muted-foreground">
                        {formatTrackingTime(event.at)}
                        {event.location ? ` · ${placeOf(event)}` : ''}
                        {stop != null ? ` · Stop ${stop}` : ''}
                      </p>
                      <p className="break-words">{scanOf(event)}</p>
                    </>
                  )
                  return (
                    <li
                      key={event.id}
                      className="relative"
                      aria-current={highlighted ? 'true' : undefined}
                    >
                      <span
                        className={cn(
                          'absolute top-1.5 -left-[21px] size-2.5 rounded-full border-2 bg-background',
                          index === 0
                            ? 'border-route-current bg-route-current'
                            : 'border-route-actual dark:border-moving',
                        )}
                        aria-hidden
                      />
                      {stop != null ? (
                        <button
                          type="button"
                          onClick={() => pick(stop)}
                          className={cn(
                            '-mx-2 block w-[calc(100%+1rem)] rounded-md px-2 py-0.5 text-left transition-colors outline-none hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50',
                            highlighted && 'bg-muted',
                          )}
                        >
                          {content}
                        </button>
                      ) : (
                        content
                      )}
                    </li>
                  )
                })}
              </ol>
              {tracking.events.length > 4 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-2 -ml-2 h-10 sm:h-8"
                  onClick={() => setShowAll((value) => !value)}
                >
                  {showAll ? 'Show fewer updates' : `Show all ${tracking.events.length} updates`}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {tracking.registration && (
        <div className="space-y-2 border-t pt-3 text-xs text-muted-foreground">
          <p>
            {REGISTRATION_LABELS[tracking.registration.status]}
            {tracking.provider
              ? ` · ${PROVIDER_LABELS[tracking.provider] ?? tracking.provider}`
              : ''}
            {tracking.registration.attempts > 1 ? ` · ${tracking.registration.attempts} tries` : ''}
            {tracking.updated_at ? ` · last news ${formatTrackingTime(tracking.updated_at)}` : ''}
          </p>
          {tracking.registration.last_error && (
            <p className="text-destructive">{tracking.registration.last_error}</p>
          )}
          {onRefresh && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 sm:h-8"
              onClick={refresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {tracking.registration.status === 'REGISTERED' ? 'Check now' : 'Register again'}
            </Button>
          )}
          {refreshError && (
            <p role="alert" className="text-destructive">
              {refreshError}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
